// src/services/dashboardData.js
// Mock data service for dashboard analytics
// This will be replaced with real API calls later

/**
 * Generate mock dashboard data
 * @param {string} dealershipId - Optional dealership ID for filtering
 * @returns {Object} Dashboard data including KPIs and chart data
 */
export const fetchDashboardData = (dealershipId = null) => {
  // Mock data - replace with actual API call later
  const today = new Date();
  const last30Days = Array.from({ length: 30 }, (_, i) => {
    const date = new Date(today);
    date.setDate(date.getDate() - (29 - i));
    return date.toISOString().split('T')[0];
  });

  // Generate daily appointments data for the last 30 days
  const dailyAppointments = last30Days.map(date => {
    const baseAppointments = Math.floor(Math.random() * 15) + 5;
    const completed = Math.floor(baseAppointments * (0.6 + Math.random() * 0.3));
    const noShows = Math.floor(baseAppointments * (0.05 + Math.random() * 0.15));

    return {
      date,
      appointments: baseAppointments,
      completed,
      noShows,
      pending: baseAppointments - completed - noShows
    };
  });

  // Today's data
  const todayData = dailyAppointments[dailyAppointments.length - 1];
  const yesterdayData = dailyAppointments[dailyAppointments.length - 2];

  // KPIs
  const todayAppointments = todayData.appointments;
  const yesterdayAppointments = yesterdayData.appointments;
  const appointmentsTrend = ((todayAppointments - yesterdayAppointments) / yesterdayAppointments * 100).toFixed(1);

  const confirmed = todayAppointments - todayData.pending;
  const showRate = confirmed > 0 ? ((todayData.completed / confirmed) * 100).toFixed(1) : 0;
  const yesterdayConfirmed = yesterdayAppointments - yesterdayData.pending;
  const yesterdayShowRate = yesterdayConfirmed > 0 ? ((yesterdayData.completed / yesterdayConfirmed) * 100) : 0;
  const showRateTrend = yesterdayShowRate > 0 ? ((showRate - yesterdayShowRate) / yesterdayShowRate * 100).toFixed(1) : 0;

  const completedToday = todayData.completed;
  const completedYesterday = yesterdayData.completed;
  const completedTrend = completedYesterday > 0 ? ((completedToday - completedYesterday) / completedYesterday * 100).toFixed(1) : 0;

  const noShowsToday = todayData.noShows;
  const noShowsYesterday = yesterdayData.noShows;
  const noShowsTrend = noShowsYesterday > 0 ? ((noShowsToday - noShowsYesterday) / noShowsYesterday * 100).toFixed(1) : 0;

  // Status funnel data
  const statusFunnel = {
    pending: 12,
    confirmed: 35,
    vehicleReady: 18,
    checkedIn: 8,
    completed: 142,
    canceled: 15,
    noShow: 23
  };

  // Appointments by day of week
  const dayOfWeekData = [
    { day: 'Mon', appointments: 45, completed: 32, noShows: 5 },
    { day: 'Tue', appointments: 52, completed: 38, noShows: 7 },
    { day: 'Wed', appointments: 48, completed: 35, noShows: 6 },
    { day: 'Thu', appointments: 56, completed: 42, noShows: 8 },
    { day: 'Fri', appointments: 62, completed: 45, noShows: 9 },
    { day: 'Sat', appointments: 78, completed: 58, noShows: 12 },
    { day: 'Sun', appointments: 42, completed: 30, noShows: 6 }
  ];

  // Top vehicles
  const topVehicles = [
    {
      vehicle: '2024 Tesla Model 3',
      appointments: 87,
      completed: 68,
      noShows: 12,
      completionRate: 78.2,
      noShowRate: 13.8
    },
    {
      vehicle: '2024 BMW X5',
      appointments: 76,
      completed: 62,
      noShows: 8,
      completionRate: 81.6,
      noShowRate: 10.5
    },
    {
      vehicle: '2024 Audi e-tron',
      appointments: 65,
      completed: 51,
      noShows: 9,
      completionRate: 78.5,
      noShowRate: 13.8
    },
    {
      vehicle: '2024 Mercedes EQS',
      appointments: 58,
      completed: 48,
      noShows: 6,
      completionRate: 82.8,
      noShowRate: 10.3
    },
    {
      vehicle: '2024 Porsche Taycan',
      appointments: 54,
      completed: 43,
      noShows: 7,
      completionRate: 79.6,
      noShowRate: 13.0
    }
  ];

  // Check-in time distribution (hours of the day)
  const checkInTimes = [
    { hour: '8 AM', count: 2 },
    { hour: '9 AM', count: 8 },
    { hour: '10 AM', count: 15 },
    { hour: '11 AM', count: 22 },
    { hour: '12 PM', count: 18 },
    { hour: '1 PM', count: 25 },
    { hour: '2 PM', count: 28 },
    { hour: '3 PM', count: 32 },
    { hour: '4 PM', count: 24 },
    { hour: '5 PM', count: 19 },
    { hour: '6 PM', count: 12 },
    { hour: '7 PM', count: 5 }
  ];

  return {
    kpis: {
      todayBookings: {
        value: todayAppointments,
        trend: parseFloat(appointmentsTrend),
        label: "Today's Appointments"
      },
      showRate: {
        value: parseFloat(showRate),
        trend: parseFloat(showRateTrend),
        label: 'Show Rate (Today)',
        suffix: '%'
      },
      completedToday: {
        value: completedToday,
        trend: parseFloat(completedTrend),
        label: 'Completed Test Drives'
      },
      noShowsToday: {
        value: noShowsToday,
        trend: parseFloat(noShowsTrend),
        label: 'No Shows (Today)'
      }
    },
    dailyBookings: dailyAppointments,
    statusFunnel,
    dayOfWeekData,
    topVehicles,
    checkInTimes
  };
};

/**
 * Get list of dealerships (for admin role)
 * @returns {Array} List of dealerships
 */
export const fetchDealerships = () => {
  return [
    { id: 'dealer1', name: 'Downtown Auto Center' },
    { id: 'dealer2', name: 'Westside Motors' },
    { id: 'dealer3', name: 'Eastside Dealership' },
    { id: 'dealer4', name: 'North Point Auto' },
    { id: 'dealer5', name: 'South Bay Cars' }
  ];
};
